import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label { display: block; margin-bottom: 0.5rem; font-weight:500; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const AdminItem = styled.div`
  position: relative;
  height: 150px;
  border-radius: 4px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  button {
    position: absolute;
    top: 5px;
    right: 5px;
    background: red;
    color: white;
    border: none;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const GalleryAdmin = () => {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ caption: '', images: [] });
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/gallery');
      setItems(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (files) => {
    if (!files) {
      setNewItem({ ...newItem, images: [] });
      setPreviews([]);
      return;
    }
    // Handle both single file (if ImageUploader returns single) and array
    const fileList = Array.isArray(files) ? files : [files];
    setNewItem({ ...newItem, images: fileList });
    
    // Revoke old object URLs to avoid memory leaks
    previews.forEach(url => URL.revokeObjectURL(url));
    
    const newPreviews = fileList.map(file => URL.createObjectURL(file));
    setPreviews(newPreviews);
  };

  const handleAdd = async () => {
    if (newItem.images.length === 0) return alert('Please select images');
    setUploading(true);
    setProgress(0);
    
    const timer = setInterval(() => {
      setProgress(old => {
        if (old >= 90) return 90;
        return old + 10;
      });
    }, 100);

    try {
      const formData = new FormData();
      newItem.images.forEach(file => {
        formData.append('images', file);
      });
      formData.append('caption', newItem.caption);
      
      await api.upload('/gallery', formData);
      
      clearInterval(timer);
      setProgress(100);
      
      setTimeout(() => {
        setNewItem({ caption: '', images: [] });
        setPreviews([]);
        setUploading(false);
        setProgress(0);
        loadData();
      }, 500);
    } catch (err) {
      clearInterval(timer);
      console.error(err);
      alert('Failed to upload');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/gallery/${id}`);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Container>
      <h2>Manage Gallery</h2>

      <Card>
        <CardContent>
          <h3>Add New Image</h3>
          <FormGroup>
            <label>Images</label>
            <ImageUploader onImageSelect={handleImageSelect} multiple={true} />
            {previews.length > 0 && (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '1rem'}}>
                    {previews.map((url, idx) => (
                        <div key={idx} style={{width:'100px', height:'100px'}}>
                            <img src={url} alt="" style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:'4px'}} />
                        </div>
                    ))}
                </div>
            )}
          </FormGroup>
          <FormGroup>
            <label>Caption</label>
            <Input 
                value={newItem.caption} 
                onChange={(e) => setNewItem({...newItem, caption: e.target.value})} 
                placeholder="Optional caption"
            />
          </FormGroup>
          <Button onClick={handleAdd} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Add to Gallery'}
          </Button>
          {uploading && <ProgressBar progress={progress} />}
        </CardContent>
      </Card>

      <Grid>
        {items.map(item => (
          <AdminItem key={item._id}>
            <img src={getImageUrl(item.image)} alt={item.caption} />
            <button onClick={() => handleDelete(item._id)}>x</button>
            {item.caption && <div style={{position:'absolute', bottom:0, background:'rgba(0,0,0,0.5)', color:'white', width:'100%', fontSize:'0.8rem', padding:'0.2rem'}}>{item.caption}</div>}
          </AdminItem>
        ))}
      </Grid>
    </Container>
  );
};

export default GalleryAdmin;
